import React from 'react';
import { FaStar, FaLightbulb } from 'react-icons/fa';

const ArchetypeResult = ({ archetype, onClose, onComplete }) => {
    const getArchetypeColor = (nivel) => {
        switch (nivel) {
            case 'TRADICIONALES RESISTENTES':
                return '#FF6B6B';
            case 'EN TRANSICIÓN':
                return '#4ECDC4';
            case 'FUNCIONALES CON POTENCIAL':
                return '#45B7D1';
            case 'EARLY ADOPTERS Y ESCALADORES':
                return '#96CEB4';
            default:
                return '#6B7280';
        }
    };

    const getArchetypeIcon = (nivel) => {
        switch (nivel) {
            case 'TRADICIONALES RESISTENTES':
                return '🏪';
            case 'EN TRANSICIÓN':
                return '🔄';
            case 'FUNCIONALES CON POTENCIAL':
                return '📈';
            case 'EARLY ADOPTERS Y ESCALADORES':
                return '🚀';
            default:
                return '💼';
        }
    };

    const getRecommendations = (nivel) => {
        const recommendations = {
            'TRADICIONALES RESISTENTES': [
                'Comienza con herramientas básicas como WhatsApp Business',
                'Implementa un sistema de fidelización simple',
                'Crea contenido básico para redes sociales',
                'Aprende a usar un CRM básico'
            ],
            'EN TRANSICIÓN': [
                'Automatiza procesos de marketing',
                'Implementa email marketing',
                'Crea campañas de retargeting',
                'Analiza métricas básicas'
            ],
            'FUNCIONALES CON POTENCIAL': [
                'Optimiza campañas existentes',
                'Implementa análisis avanzado',
                'Automatiza más procesos',
                'Integra múltiples canales'
            ],
            'EARLY ADOPTERS Y ESCALADORES': [
                'Implementa IA en marketing',
                'Usa integraciones avanzadas',
                'Optimiza para conversión',
                'Escala a múltiples mercados'
            ]
        };

        return recommendations[nivel] || [];
    };

    const getNextSteps = (nivel) => {
        const nextSteps = {
            'TRADICIONALES RESISTENTES': [
                { title: 'Configurar WhatsApp Business', icon: '📱', priority: 'Alta' },
                { title: 'Crear primera campaña de fidelización', icon: '🎯', priority: 'Alta' },
                { title: 'Configurar redes sociales básicas', icon: '📢', priority: 'Media' }
            ],
            'EN TRANSICIÓN': [
                { title: 'Configurar email marketing', icon: '📧', priority: 'Alta' },
                { title: 'Implementar automatizaciones', icon: '⚙️', priority: 'Alta' },
                { title: 'Analizar métricas básicas', icon: '📊', priority: 'Media' }
            ],
            'FUNCIONALES CON POTENCIAL': [
                { title: 'Optimizar campañas existentes', icon: '🎯', priority: 'Alta' },
                { title: 'Implementar análisis avanzado', icon: '📈', priority: 'Alta' },
                { title: 'Integrar múltiples canales', icon: '🔗', priority: 'Media' }
            ],
            'EARLY ADOPTERS Y ESCALADORES': [
                { title: 'Implementar IA en marketing', icon: '🤖', priority: 'Alta' },
                { title: 'Configurar integraciones avanzadas', icon: '🔧', priority: 'Alta' },
                { title: 'Optimizar para conversión', icon: '💰', priority: 'Media' }
            ]
        };

        return nextSteps[nivel] || [];
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                        ¡Configuración Completada!
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors text-xl"
                    >
                        ✕
                    </button>
                </div>

                {/* Success Message */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6 border-l-4 border-green-500">
                    <div className="flex items-center mb-4">
                        <span className="text-4xl mr-4">✅</span>
                        <div>
                            <h3 className="text-xl font-bold text-gray-800 mb-1">
                                Configuración Completada
                            </h3>
                            <p className="text-sm text-gray-600">
                                Tu perfil ha sido configurado correctamente
                            </p>
                        </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-4">
                        Hemos configurado tu perfil para personalizar tu experiencia en PetitPrime.
                        Ahora puedes acceder a todas las funcionalidades con recomendaciones adaptadas a tu negocio.
                    </p>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <FaStar className="text-yellow-500 mr-2" />
                            <span className="text-sm text-gray-600">
                                Configuración completada exitosamente
                            </span>
                        </div>
                        <div className="text-sm text-gray-500">
                            {new Date(archetype.completedAt).toLocaleDateString()}
                        </div>
                    </div>
                </div>

                {/* Welcome Message */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <div className="flex items-center mb-4">
                        <FaLightbulb className="text-blue-500 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-800">
                            ¡Bienvenido a PetitPrime!
                        </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                        Tu configuración ha sido completada exitosamente. Ahora puedes:
                    </p>
                    <ul className="space-y-2 mt-4">
                        <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span className="text-gray-700">Acceder a todas las funcionalidades de marketing</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span className="text-gray-700">Recibir recomendaciones personalizadas</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span className="text-gray-700">Gestionar campañas y clientes</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span className="text-gray-700">Analizar métricas y resultados</span>
                        </li>
                    </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center items-center mt-6 pt-6 border-t">
                    <button
                        onClick={() => {
                            if (onComplete) {
                                onComplete(archetype);
                            }
                            onClose();
                        }}
                        className="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                    >
                        Continuar al Dashboard
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArchetypeResult; 